import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  ArrowRight,
  Code,
  Smartphone,
  Cloud,
  Database,
  Lock,
  Zap,
} from 'lucide-react';

const CustomDevelopmentPage = () => {
  const { t } = useTranslation();

  const services = [
    {
      icon: Code,
      title: t('cdPage.s1Title'),
      description: t('cdPage.s1Desc'),
      technologies: ['React', 'Node.js', 'Python', 'PostgreSQL'],
    },
    {
      icon: Smartphone,
      title: t('cdPage.s2Title'),
      description: t('cdPage.s2Desc'),
      technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin'],
    },
    {
      icon: Cloud,
      title: t('cdPage.s3Title'),
      description: t('cdPage.s3Desc'),
      technologies: ['AWS', 'Azure', 'GCP', 'Docker'],
    },
    {
      icon: Database,
      title: t('cdPage.s4Title'),
      description: t('cdPage.s4Desc'),
      technologies: ['REST', 'GraphQL', 'WebSocket', 'gRPC'],
    },
    {
      icon: Lock,
      title: t('cdPage.s5Title'),
      description: t('cdPage.s5Desc'),
      technologies: ['OAuth', 'GDPR', 'SOC 2', 'ISO 27001'],
    },
    {
      icon: Zap,
      title: t('cdPage.s6Title'),
      description: t('cdPage.s6Desc'),
      technologies: ['Redis', 'CDN', 'Caching', 'Load Balancing'],
    },
  ];

  const process = [
    {
      step: t('cdPage.pr1Step'),
      description: t('cdPage.pr1Desc'),
      duration: t('cdPage.pr1Duration'),
      deliverables: [t('cdPage.pr1d1'), t('cdPage.pr1d2'), t('cdPage.pr1d3'), t('cdPage.pr1d4')],
    },
    {
      step: t('cdPage.pr2Step'),
      description: t('cdPage.pr2Desc'),
      duration: t('cdPage.pr2Duration'),
      deliverables: [t('cdPage.pr2d1'), t('cdPage.pr2d2'), t('cdPage.pr2d3'), t('cdPage.pr2d4')],
    },
    {
      step: t('cdPage.pr3Step'),
      description: t('cdPage.pr3Desc'),
      duration: t('cdPage.pr3Duration'),
      deliverables: [t('cdPage.pr3d1'), t('cdPage.pr3d2'), t('cdPage.pr3d3'), t('cdPage.pr3d4')],
    },
    {
      step: t('cdPage.pr4Step'),
      description: t('cdPage.pr4Desc'),
      duration: t('cdPage.pr4Duration'),
      deliverables: [t('cdPage.pr4d1'), t('cdPage.pr4d2'), t('cdPage.pr4d3'), t('cdPage.pr4d4')],
    },
    {
      step: t('cdPage.pr5Step'),
      description: t('cdPage.pr5Desc'),
      duration: t('cdPage.pr5Duration'),
      deliverables: [t('cdPage.pr5d1'), t('cdPage.pr5d2'), t('cdPage.pr5d3'), t('cdPage.pr5d4')],
    },
    {
      step: t('cdPage.pr6Step'),
      description: t('cdPage.pr6Desc'),
      duration: t('cdPage.pr6Duration'),
      deliverables: [t('cdPage.pr6d1'), t('cdPage.pr6d2'), t('cdPage.pr6d3'), t('cdPage.pr6d4')],
    },
  ];

  const portfolio = [
    {
      title: t('cdPage.pf1Title'),
      category: 'Gold Retail',
      description: t('cdPage.pf1Desc'),
      technologies: ['Laravel', 'iOS', 'Android'],
      results: ['Backoffice + Mobile App', 'Factory Order Management', 'Stage-based Assignment'],
    },
    {
      title: t('cdPage.pf2Title'),
      category: 'Healthcare',
      description: t('cdPage.pf2Desc'),
      technologies: ['Laravel', 'iOS', 'Android'],
      results: ['Multi-clinic Management', 'KSA & Beirut', 'Backoffice + Mobile App'],
    },
    {
      title: t('cdPage.pf3Title'),
      category: 'Finance',
      description: t('cdPage.pf3Desc'),
      technologies: ['.NET Core'],
      results: ['MT5 Bridge Integration', 'Real-time Trade Execution', 'Custom Broker Solution'],
    },
  ];

  const pricing = [
    {
      name: t('cdPage.pl1Name'),
      price: '$15K - $50K',
      description: t('cdPage.pl1Desc'),
      popular: false,
      features: [
        '4-8 weeks timeline',
        'Core features only',
        'Web or mobile',
        'Basic design',
        'Essential integrations',
        '3 months support',
      ],
    },
    {
      name: t('cdPage.pl2Name'),
      price: '$50K - $150K',
      popular: true,
      description: t('cdPage.pl2Desc'),
      features: [
        '12-20 weeks timeline',
        'Full feature set',
        'Web + mobile',
        'Custom design',
        'Third-party integrations',
        'Advanced security',
        '6 months support',
        'Performance optimization',
      ],
    },
    {
      name: t('cdPage.pl3Name'),
      price: 'Custom Quote',
      description: t('cdPage.pl3Desc'),
      popular: false,
      features: [
        'Custom timeline',
        'Unlimited features',
        'Multi-platform',
        'Enterprise design system',
        'Complex integrations',
        'Compliance (HIPAA, SOC 2)',
        'Dedicated team',
        '12+ months support',
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Custom Software Development | mwbTechs</title>
        <meta
          name="description"
          content="Professional custom software development services. Web apps, mobile apps, APIs, and cloud solutions built with modern technologies."
        />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        {/* Hero Section */}
        <section className="section-sm bg-gradient-to-br from-secondary-50 to-purple-50">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 bg-secondary-100 text-secondary-700 px-4 py-2 rounded-full mb-6">
                  <Code className="w-4 h-4" />
                  <span className="font-medium">{t('cdPage.badge')}</span>
                </div>
                <h1 className="text-5xl font-bold text-gray-900 mb-6">
                  {t('cdPage.heroTitle')}
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  {t('cdPage.heroSubtitle')}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/contact" className="btn btn-secondary text-lg">
                    {t('cdPage.requestQuote')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                  <a href="#portfolio" className="btn btn-outline text-lg">
                    {t('cdPage.viewPortfolio')}
                  </a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200"
              >
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{t('cdPage.stat2Title')}</div>
                      <div className="text-sm text-gray-600">{t('cdPage.stat2Sub')}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Zap className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{t('cdPage.stat3Title')}</div>
                      <div className="text-sm text-gray-600">{t('cdPage.stat3Sub')}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="section">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('cdPage.servicesTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('cdPage.servicesSubtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-all"
                  >
                    <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-secondary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {service.technologies.map((tech, i) => (
                        <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="section bg-gray-50">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('cdPage.processTitle')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('cdPage.processSubtitle')}
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-6">
              {process.map((phase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-secondary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-4 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{phase.step}</h3>
                      </div>
                      <p className="text-gray-600 mb-4">{phase.description}</p>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {phase.deliverables.map((item, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Portfolio Section */}
        <section id="portfolio" className="section">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('cdPage.portfolioTitle')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('cdPage.portfolioSubtitle')}
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {portfolio.map((project, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="aspect-video bg-gradient-to-br from-secondary-100 to-purple-100"></div>
                  <div className="p-6">
                    <span className="inline-block bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full text-sm font-medium mb-3">
                      {project.category}
                    </span>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                    <p className="text-gray-600 mb-4">{project.description}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.technologies.map((tech, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                          {tech}
                        </span>
                      ))}
                    </div>
                    <div className="space-y-1">
                      {project.results.map((result, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-gray-700">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="section bg-gray-50">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('cdPage.investmentTitle')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('cdPage.investmentSubtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {pricing.map((plan, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`pricing-card ${plan.popular ? 'pricing-card-featured' : ''}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-secondary-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      {t('cdPage.mostPopular')}
                    </div>
                  )}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 text-sm">{plan.description}</p>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className={`w-full btn ${plan.popular ? 'btn-secondary' : 'btn-outline'}`}
                  >
                    {t('cdPage.getQuote')}
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-sm bg-gradient-to-r from-secondary-600 to-purple-600 text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl font-bold mb-4">
              {t('cdPage.ctaTitle')}
            </h2>
            <p className="text-xl text-purple-100 mb-8">
              {t('cdPage.ctaSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact" className="btn btn-white text-lg">
                {t('cdPage.requestFreeQuote')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CustomDevelopmentPage;
